import { AbstractControl, FormArray, ValidatorFn } from '@angular/forms';

export const PHONE_MIN_DIGITS = 8;
export const PHONE_MAX_DIGITS = 15;
export const DOCUMENT_FILE_TYPE_REGEX = /^.+\.(pdf|png|jpg|jpeg)$/i;
export const DOB_DD_MM_YYYY_REGEX = /^(0[1-9]|[12]\d|3[01])-(0[1-9]|1[0-2])-(\d{4})$/;
export const TENANT_USER_CREDITS_MAX = 100000;
export const TENANT_DURATION_MONTHS_MAX = 120;

export function normalizePhone(raw: string): string {
  return raw.toString().trim().replace(/\s+/g, '');
}

export function urlValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.trim();
  const urlRegex = /^(?=.{1,254}$)https:\/\/([A-Za-z0-9-]+\.)+[A-Za-z]{2,}(\/[^\s]*)?$/;

  if (!urlRegex.test(value)) {
    return { url: true };
  }

  try {
    const urlObj = new URL(value);
    if (!urlObj.hostname.includes('.')) {
      return { url: true };
    }
    return null;
  } catch {
    return { url: true };
  }
}

export function requiredValidator(control: AbstractControl) {
  if (control.value === null || control.value === undefined) {
    return { required: true };
  }

  if (control.value.toString().trim().length === 0) {
    return { required: true };
  }

  return null;
}

export function requiredBooleanValidator(control: AbstractControl) {
  if (control.value === null || control.value === undefined) {
    return { required: true };
  }
  return null;
}

export function emailValidator(control: AbstractControl) {
  if (!control.value) return null;
  const emailRegex = /^(?=.{1,254}$)(?=.{1,64}@)[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*(\.[A-Za-z]{2,})$/;
  return emailRegex.test(control.value.trim()) ? null : { email: true };
}

export function lettersOnlyValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const nameRegex = /^(?=.{2,50}$)[A-Za-z .'-]+$/;
  return nameRegex.test(value) ? null : { lettersOnly: true };
}

/**
 * Punctuation a password's fourth character class is drawn from. Named out
 * rather than matched as "anything that is not a letter or a digit", which let a
 * plain space count towards the requirement.
 */
export const PASSWORD_SPECIAL_CHARACTERS = '!@#$%^&*()_+-=[]{};:\'",.<>/?\\|`~';

export function passwordValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString();
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};:'",.<>/?\\|`~]).{8,}$/;
  return passwordRegex.test(value) ? null : { password: true };
}

export function companyNameOrTitleValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const companyRegex = /^(?=.{1,100}$).+$/;
  return companyRegex.test(value) ? null : { companyNameOrTitle: true };
}

export function cityValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const cityRegex = /^(?=.{1,100}$)[A-Za-z]+$/;
  return cityRegex.test(value) ? null : { city: true };
}

export function phoneValidator(control: AbstractControl) {
  if (!control.value) return null;
  const normalized = normalizePhone(String(control.value));
  const phoneRegex = /^\+?\d{10,14}$/;
  return phoneRegex.test(normalized) ? null : { phone: { format: true } };
}

export function telephoneValidator(control: AbstractControl) {
  if (!control.value) return null;
  const normalized = normalizePhone(String(control.value));
  const phoneRegex = /^\+?\d{10,14}$/;
  return phoneRegex.test(normalized) ? null : { telephone: { format: true } };
}

export function accountNumberValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const accountNumberRegex = /^\d{8}$/;
  return accountNumberRegex.test(value) ? null : { accountNumber: true };
}

export function sortCodeValidator(control: AbstractControl) {
  if (!control.value) return null;

  const value = control.value.toString().trim();

  // Accepts: 123456, 12-34-56, 12 34 56
  const sortCodeRegex = /^(\d{6}|\d{2}[- ]?\d{2}[- ]?\d{2})$/;

  return sortCodeRegex.test(value) ? null : { sortCode: true };
}

export function fileTypeValidator(allowedTypes: string[]): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;

    const fileName =
      control.value instanceof File
        ? control.value.name
        : control.value.toString().trim();

    if (!fileName) {
      return null;
    }

    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    const isValid = allowedTypes.some((type) => {
      const normalized = type.toLowerCase().replace('.', '');
      return !!fileExtension && fileExtension === normalized;
    });

    return isValid ? null : { fileType: { allowedTypes } };
  };
}

export function documentFileTypeValidator(control: AbstractControl) {
  if (!control.value) return null;

  const fileName =
    control.value instanceof File
      ? control.value.name
      : control.value.toString().trim();

  return DOCUMENT_FILE_TYPE_REGEX.test(fileName) ? null : { documentFileType: true };
}

export function fileSizeValidator(maxSizeMB: number): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value || !(control.value instanceof File)) return null;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return control.value.size <= maxSizeBytes ? null : { fileSize: { maxSizeMB } };
  };
}

export function minLengthValidator(min: number): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;
    const trimmedValue = control.value.toString().trim();
    return trimmedValue.length >= min
      ? null
      : {
          minlength: {
            requiredLength: min,
            actualLength: trimmedValue.length
          }
        };
  };
}

export function maxLengthValidator(max: number): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;
    const trimmedValue = control.value.toString().trim();
    return trimmedValue.length <= max
      ? null
      : {
          maxlength: {
            requiredLength: max,
            actualLength: trimmedValue.length
          }
        };
  };
}

export function companyNumberValidator(control: AbstractControl) {
  if (!control.value) return null;
  const regex = /^(?:\d{8}|[A-Za-z]{2}\d{6})$/;
  return regex.test(control.value.trim()) ? null : { companyNumber: true };
}

export function fcaFirmReferenceNumberValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const regex = /^\d{6,10}$/;
  return regex.test(value) ? null : { fcaFirmReferenceNumber: true };
}

export function addressValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const regex = /^(?=.{1,255}$).+$/;
  return regex.test(value) ? null : { address: true };
}

/**
 * Deliberately permissive: 5–12 characters of letters, digits and spaces.
 *
 * The old strict mainland shape (`/^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/`)
 * could never match more than seven characters, so it rejected every longer valid
 * code — BFPO, GIR 0AA, Crown Dependency and overseas-territory postcodes. On the
 * branch form that was worse than a red field: the lookup is guarded on
 * `postCodeControl.invalid`, so an eight-character postcode silently skipped the
 * address lookup and the user got no "no addresses found" warning either.
 */
export function postcodeValidator(control: AbstractControl) {
  if (!control.value) return null;
  const regex = /^[A-Z0-9][A-Z0-9 ]{3,10}[A-Z0-9]$/i;
  return regex.test(control.value.trim().toUpperCase()) ? null : { postcode: true };
}

export function requiredDocumentsValidator(requiredTypes: string[]): ValidatorFn {
  return (formArray: AbstractControl) => {
    if (!(formArray instanceof FormArray)) return null;

    const uploadedTypes: string[] = [];
    formArray.controls.forEach((control) => {
      const docType = control.get('documentType')?.value?.value;
      if (docType && docType !== 'other') {
        uploadedTypes.push(docType);
      }
    });

    const missingDocuments = requiredTypes.filter((type) => !uploadedTypes.includes(type));

    return missingDocuments.length > 0 ? { requiredDocuments: { missing: missingDocuments } } : null;
  };
}

export function patternValidator(pattern: RegExp, errorName: string = 'pattern'): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;
    return pattern.test(control.value) ? null : { [errorName]: true };
  };
}

export function noNumbersValidator(control: AbstractControl) {
  if (!control.value) return null;
  const hasNumbers = /[0-9]/.test(control.value);
  return hasNumbers ? { hasNumbers: true } : null;
}

export function rangeValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;
    const value = Number(control.value);
    if (isNaN(value)) return { range: { min, max } };
    return value >= min && value <= max ? null : { range: { min, max, actual: value } };
  };
}

export function tenantUserCreditsValidator(min: number = 1): ValidatorFn {
  return rangeValidator(min, TENANT_USER_CREDITS_MAX);
}

export function tenantDurationMonthsValidator(min: number = 1): ValidatorFn {
  return rangeValidator(min, TENANT_DURATION_MONTHS_MAX);
}

export function conductCheckValidator(): ValidatorFn {
  return maxLengthValidator(1000);
}

export function matchValidator(controlName: string, matchingControlName: string): ValidatorFn {
  return (formGroup: AbstractControl) => {
    const control = formGroup.get(controlName);
    const matchingControl = formGroup.get(matchingControlName);

    if (!control || !matchingControl) {
      return null;
    }

    if (matchingControl.errors && !matchingControl.errors['mismatch']) {
      return null;
    }

    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    matchingControl.setErrors(null);
    return null;
  };
}

export function notFutureDateValidator(control: AbstractControl) {
  if (!control.value) return null;
  const inputDate = new Date(control.value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return inputDate <= today ? null : { futureDate: true };
}

export function notPastDateValidator(control: AbstractControl) {
  if (!control.value) return null;
  const inputDate = new Date(control.value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return inputDate >= today ? null : { pastDate: true };
}

export function minAgeValidator(minAge: number): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;
    const birthDate = new Date(control.value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= minAge ? null : { minAge: { required: minAge, actual: age } };
  };
}

export function niNumberValidator(control: AbstractControl) {
  if (!control.value) return null;

  const rawValue = control.value.toString().trim().toUpperCase();
  if (/\s/.test(rawValue)) {
    return { niNumber: true };
  }

  const niRegex = /^[A-Z]{2}\d{6}[A-Z]$/;

  if (!niRegex.test(rawValue)) {
    return { niNumber: true };
  }

  return null;
}

export function dobFormatValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  if (!DOB_DD_MM_YYYY_REGEX.test(value)) {
    return { dobFormat: true };
  }

  const [dayText, monthText, yearText] = value.split('-');
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const date = new Date(year, month - 1, day);
  const isValidDate =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  return isValidDate ? null : { dobFormat: true };
}

export function dobPastDateValidator(control: AbstractControl) {
  if (!control.value) return null;

  const value = control.value.toString().trim();
  if (!DOB_DD_MM_YYYY_REGEX.test(value)) {
    return null;
  }

  const [dayText, monthText, yearText] = value.split('-');
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const inputDate = new Date(year, month - 1, day);
  inputDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return inputDate < today ? null : { dobPastDate: true };
}

export function dobMinAgeValidator(minAge: number): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value) return null;

    const value = control.value.toString().trim();
    if (!DOB_DD_MM_YYYY_REGEX.test(value)) {
      return null;
    }

    const [dayText, monthText, yearText] = value.split('-');
    const day = Number(dayText);
    const month = Number(monthText);
    const year = Number(yearText);
    const birthDate = new Date(year, month - 1, day);

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= minAge ? null : { dobMinAge: { required: minAge, actual: age } };
  };
}
export function fullNameValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const nameRegex = /^(?=.{1,100}$)[A-Za-z]+([ .'-][A-Za-z]+)*$/;
  return nameRegex.test(value) ? null : { fullName: true };
}

export function nameValidator(control: AbstractControl) {
  if (!control.value) return null;
  const value = control.value.toString().trim();
  const companyNameRegex = /^[a-zA-Z0-9 &()\-]{2,50}$/;

  return companyNameRegex.test(value) ? null : { name: true };
}

export function descriptionValidator(control: AbstractControl) {
  if (!control.value) return null;

  const value = control.value.toString().trim();
  const descriptionRegex = /^[a-zA-Z0-9\s!"#$%&'()*+,\-./:;<=>?@\[\\\]^_`{|}~]{1,1000}$/;

  return descriptionRegex.test(value) ? null : { description: true };
}

export function absentDaysValidator(control: AbstractControl) {
  return rangeValidator(0, 730)(control);
}

export const CustomValidators = {
  PHONE_MIN_DIGITS,
  PHONE_MAX_DIGITS,
  DOCUMENT_FILE_TYPE_REGEX,
  DOB_DD_MM_YYYY_REGEX,
  TENANT_USER_CREDITS_MAX,
  TENANT_DURATION_MONTHS_MAX,
  normalizePhone,
  url: urlValidator,
  required: requiredValidator,
  requiredBoolean: requiredBooleanValidator,
  email: emailValidator,
  lettersOnly: lettersOnlyValidator,
  password: passwordValidator,
  companyNameOrTitle: companyNameOrTitleValidator,
  city: cityValidator,
  phone: phoneValidator,
  telephone: telephoneValidator,
  accountNumber: accountNumberValidator,
  sortCode: sortCodeValidator,
  fileType: fileTypeValidator,
  documentFileType: documentFileTypeValidator,
  fileSize: fileSizeValidator,
  minLength: minLengthValidator,
  maxLength: maxLengthValidator,
  companyNumber: companyNumberValidator,
  fcaFirmReferenceNumber: fcaFirmReferenceNumberValidator,
  address: addressValidator,
  postcode: postcodeValidator,
  requiredDocuments: requiredDocumentsValidator,
  pattern: patternValidator,
  noNumbers: noNumbersValidator,
  range: rangeValidator,
  tenantUserCredits: tenantUserCreditsValidator,
  tenantDurationMonths: tenantDurationMonthsValidator,
  conductCheck: conductCheckValidator,
  match: matchValidator,
  notFutureDate: notFutureDateValidator,
  notPastDate: notPastDateValidator,
  minAge: minAgeValidator,
  niNumber: niNumberValidator,
  dobFormat: dobFormatValidator,
  dobPastDate: dobPastDateValidator,
  dobMinAge: dobMinAgeValidator,
  fullName: fullNameValidator,
  name: nameValidator,
  description: descriptionValidator,
  absentDays: absentDaysValidator,
};
